import { useBookingFlowStore } from "@/stores/bookingFlowStore";
import {
  type MarketCountry,
  getCurrencyForMarket,
  getCurrencySymbolForMarket,
  getDefaultCountryCode,
  marketShowsTwint,
} from "@/config/market";

export interface UseMarketResult {
  /** Active market (country ISO-2, lower-case). */
  market: MarketCountry;
  /** True for the German market. */
  isGermany: boolean;
  /**
   * Display currency ISO code for the active market — this is the currency the
   * product prices were actually fetched in from Stripe (EUR for Germany, CHF
   * for Switzerland), so passing it to `formatPrice` labels real amounts and
   * never relabels.
   */
  currency: string;
  /** Standalone currency symbol for labels rendered outside `formatPrice`. */
  currencySymbol: string;
  /** Default phone / address country code (ISO alpha-2, upper-case). */
  defaultCountryCode: string;
  /** Whether the TWINT payment logo should be shown. */
  showsTwint: boolean;
  /**
   * Package types force-shown via `?force=mri`, overriding the market's hidden
   * set (AHD-7129). Pass to `isPackageTypeHiddenInMarket(market, type, forced)`.
   */
  forcedPackageTypes: readonly string[];
  /** Switch market (restarts the booking flow from the first step). */
  setMarket: (market: MarketCountry) => void;
}

/**
 * Convenience hook exposing everything a component needs to render the active
 * market: the display currency, country defaults and the market switcher.
 *
 * The market is the single source of truth for country-specific behaviour and
 * lives in the booking flow store (alongside `language`).
 */
export const useMarket = (): UseMarketResult => {
  const market = useBookingFlowStore((s) => s.market);
  const forcedPackageTypes = useBookingFlowStore((s) => s.forcedPackageTypes);
  const setMarket = useBookingFlowStore((s) => s.setMarket);

  return {
    market,
    isGermany: market === "de",
    currency: getCurrencyForMarket(market),
    currencySymbol: getCurrencySymbolForMarket(market),
    defaultCountryCode: getDefaultCountryCode(market),
    showsTwint: marketShowsTwint(market),
    forcedPackageTypes,
    setMarket,
  };
};
